// Upload retry logic with exponential backoff

export interface RetryOptions {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  onRetry?: (attempt: number, error: Error) => void;
}

const DEFAULT_OPTIONS: RetryOptions = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000
};

export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: Error;
  
  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      
      if (attempt < opts.maxRetries) {
        const delay = Math.min(
          opts.initialDelay * Math.pow(2, attempt),
          opts.maxDelay
        );
        
        opts.onRetry?.(attempt + 1, lastError);
        
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError!;
}

export async function uploadWithRetry(
  file: File,
  path: string,
  uploadFn: (file: File, path: string) => Promise<any>,
  onProgress?: (attempt: number) => void
): Promise<any> {
  return retryWithBackoff(
    () => uploadFn(file, path),
    {
      onRetry: (attempt) => {
        onProgress?.(attempt);
      }
    }
  );
}
